<template>
  <div 
    ref="container"
    class="pull-to-refresh-container"
    @touchstart="handleTouchStart"
    @touchmove="handleTouchMove"
    @touchend="handleTouchEnd"
  >
    <!-- Indicador de pull-to-refresh -->
    <div 
      ref="indicator"
      class="pull-to-refresh-indicator"
      :class="{ 'active': isPulling, 'refreshing': isRefreshing }"
      :style="{ transform: `translateY(${translateY}px)` }"
    >
      <div class="indicator-content">
        <svg 
          v-if="!isRefreshing" 
          class="indicator-icon" 
          :class="{ 'rotate': isPulling }"
          xmlns="http://www.w3.org/2000/svg" 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
        <svg 
          v-else 
          class="indicator-icon animate-spin" 
          xmlns="http://www.w3.org/2000/svg" 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        <span class="indicator-text">
          {{ isRefreshing ? 'Actualizando...' : (isPulling ? 'Suelta para actualizar' : 'Desliza hacia abajo para actualizar') }}
        </span>
      </div>
    </div>

    <!-- Contenido de la página -->
    <div class="pull-to-refresh-content">
      <slot></slot>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';

const props = defineProps({
  onRefresh: {
    type: Function,
    default: () => window.location.reload()
  },
  threshold: {
    type: Number,
    default: 80 // Distancia mínima para activar el refresh
  },
  resistance: {
    type: Number,
    default: 2.5 // Resistencia del scroll (cuánto se reduce el movimiento)
  }
});

const emit = defineEmits(['refresh-start', 'refresh-end']);

// Referencias
const container = ref(null);
const indicator = ref(null);

// Estado
const isPulling = ref(false);
const isRefreshing = ref(false);
const translateY = ref(0);
const startY = ref(0);
const currentY = ref(0);
const isEnabled = ref(false);

// Detectar si estamos en mobile
const isMobile = () => {
  return window.innerWidth <= 768 || 'ontouchstart' in window;
};

// Manejar inicio del touch
const handleTouchStart = (e) => {
  if (!isMobile() || isRefreshing.value) return;
  
  const touch = e.touches[0];
  startY.value = touch.clientY;
  currentY.value = touch.clientY;
  isEnabled.value = window.scrollY === 0; // Solo activar si estamos en el top
};

// Manejar movimiento del touch
const handleTouchMove = (e) => {
  if (!isEnabled.value || isRefreshing.value) return;
  
  const touch = e.touches[0];
  currentY.value = touch.clientY;
  
  const deltaY = currentY.value - startY.value;
  
  if (deltaY > 0 && window.scrollY === 0) {
    e.preventDefault(); // Prevenir scroll nativo
    
    const pullDistance = Math.min(deltaY / props.resistance, props.threshold * 1.5);
    translateY.value = pullDistance;
    
    isPulling.value = pullDistance >= props.threshold;
  }
};

// Manejar fin del touch
const handleTouchEnd = async () => {
  if (!isEnabled.value || isRefreshing.value) return;
  
  if (isPulling.value) {
    // Activar refresh
    isRefreshing.value = true;
    isPulling.value = false;
    emit('refresh-start');
    
    try {
      // Usar la función onRefresh si se proporciona, sino recargar la página
      if (props.onRefresh) {
        await props.onRefresh();
      } else {
        // Recarga completa de la página como F5
        window.location.reload();
      }
    } catch (error) {
      console.error('Error durante el refresh:', error);
      // Si hay error, también recargar la página
      window.location.reload();
    }
  }
  
  // Resetear posición
  translateY.value = 0;
  isEnabled.value = false;
};

// Escuchar cambios en el scroll para habilitar/deshabilitar
const handleScroll = () => {
  if (window.scrollY === 0) {
    isEnabled.value = true;
  }
};

onMounted(() => {
  if (isMobile()) {
    window.addEventListener('scroll', handleScroll);
  }
});

onUnmounted(() => {
  if (isMobile()) {
    window.removeEventListener('scroll', handleScroll);
  }
});
</script>

<style scoped>
.pull-to-refresh-container {
  position: relative;
  width: 100%;
  min-height: 100vh;
}

.pull-to-refresh-indicator {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 60px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  transform: translateY(-100%);
  transition: transform 0.3s ease;
}

.pull-to-refresh-indicator.active {
  background: rgba(227, 132, 0, 0.1);
  border-bottom-color: rgba(227, 132, 0, 0.2);
}

.pull-to-refresh-indicator.refreshing {
  background: rgba(227, 132, 0, 0.15);
  border-bottom-color: rgba(227, 132, 0, 0.3);
}

.indicator-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.indicator-icon {
  width: 20px;
  height: 20px;
  color: #666;
  transition: transform 0.3s ease;
}

.indicator-icon.rotate {
  transform: rotate(180deg);
  color: #E38400;
}

.indicator-text {
  font-size: 12px;
  color: #666;
  font-weight: 500;
  text-align: center;
}

.pull-to-refresh-indicator.active .indicator-text,
.pull-to-refresh-indicator.refreshing .indicator-text {
  color: #E38400;
}

.pull-to-refresh-content {
  width: 100%;
}

/* Animación de spin */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* Solo mostrar en mobile */
@media (min-width: 769px) {
  .pull-to-refresh-indicator {
    display: none;
  }
}
</style> 